/**
 * Offline sync queue -- stores operations in IndexedDB
 * and replays them when the device comes back online.
 */
import { openDB, withStore, STORE_NAME } from './idbHelper.js';
import { saveBackup, detectLostOperations, getLocalStorageBackup, processOcrVerify } from './syncBackup.js';

// Re-export backup utilities for external use
export { getLocalStorageBackup } from './syncBackup.js';

/**
 * Check if there are lost operations (in localStorage but not in IndexedDB).
 * @returns {Promise<{lost: boolean, lostOps: Array}>}
 */
export async function checkForLostOperations() {
  try {
    const pending = await getPending();
    return detectLostOperations(pending);
  } catch (_err) {
    const backup = getLocalStorageBackup();
    return { lost: backup.length > 0, lostOps: backup };
  }
}

/**
 * Add an operation to the offline queue
 * @param {{ type: string, url: string, method: string, body: any }} op
 * @returns {Promise<number>} The ID of the queued operation
 */
export async function addToQueue(op) {
  const id = await withStore('readwrite', (store) =>
    store.add({
      type: op.type,
      url: op.url,
      method: op.method || 'POST',
      body: op.body,
      createdAt: new Date().toISOString(),
      synced: 0,
    })
  );
  const pending = await getPending();
  saveBackup(pending);
  return id;
}

/**
 * Get all pending (unsynced) operations
 * @returns {Promise<Array<object>>}
 */
export async function getPending() {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('synced');
    const request = index.getAll(0);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Mark an operation as synced
 * @param {number} id
 * @returns {Promise<void>}
 */
async function markSynced(id) {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const record = getReq.result;
      if (record) {
        record.synced = 1;
        const putReq = store.put(record);
        putReq.onsuccess = () => resolve();
        putReq.onerror = () => reject(putReq.error);
      } else {
        resolve();
      }
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

/**
 * Get the count of pending operations
 * @returns {Promise<number>}
 */
export async function getPendingCount() {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('synced');
    const request = index.count(0);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Process the queue: send all pending operations to the server.
 * Handles OCR verification operations specially.
 * @param {string} token - Auth token
 * @param {string} campaignId - Active campaign ID
 * @returns {Promise<{ sent: number, failed: number }>}
 */
export async function processQueue(token, campaignId) {
  if (!navigator.onLine) return { sent: 0, failed: 0 };

  const pending = await getPending();
  let sent = 0;
  let failed = 0;

  for (const op of pending) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Campaign-Id': campaignId,
      };

      if (op.type === 'ocr_verify') {
        await processOcrVerify(op, headers);
        await markSynced(op.id);
        sent++;
        continue;
      }

      const response = await fetch(op.url, {
        method: op.method,
        headers,
        body: JSON.stringify(op.body),
      });
      if (response.ok) {
        await markSynced(op.id);
        sent++;
      } else {
        failed++;
      }
    } catch (_networkErr) {
      failed++;
      break;
    }
  }

  saveBackup(await getPending());
  return { sent, failed };
}

/** @type {(() => void)|null} */
let onlineListener = null;

/**
 * Start listening for online events to trigger sync
 * @param {string} token
 * @param {string} campaignId
 * @param {(result: {sent: number, failed: number}) => void} [onSync]
 */
export function startAutoSync(token, campaignId, onSync) {
  stopAutoSync();
  onlineListener = async () => {
    const result = await processQueue(token, campaignId);
    if (onSync) onSync(result);
  };
  window.addEventListener('online', onlineListener);
}

/**
 * Stop listening for online events
 */
export function stopAutoSync() {
  if (onlineListener) {
    window.removeEventListener('online', onlineListener);
    onlineListener = null;
  }
}
